/**
 * The word the bay and location pages use for one bookable resource (DNKADM-0495).
 *
 * A location may name its resources (`resourceNounSingular` / `resourceNounPlural`,
 * DNKADM-0267). When every location in play agrees on a word, the page uses it; when none
 * sets one, or they disagree, it is "bay" (the same default the booking dialog uses).
 * The plural is the location's own plural when given, never derived by adding "s" to a
 * custom singular.
 */
import type { V2Location } from '@/api/locations';

type NounSource = Pick<V2Location, 'resourceNounSingular' | 'resourceNounPlural'>;

/** The one value every entry agrees on, or null (none set, or they differ). */
function agreed(values: (string | undefined)[]): string | null {
  const set = new Set(values.map((v) => v?.trim() ?? '').filter((v) => v !== ''));
  return set.size === 1 ? [...set][0]! : null;
}

/** Lower-case singular: the shared location word, else "bay". */
export function bayNoun(locations: readonly NounSource[]): string {
  return agreed(locations.map((l) => l.resourceNounSingular))?.toLowerCase() ?? 'bay';
}

/** Lower-case plural: the shared location plural, else "bays". */
export function bayNounPlural(locations: readonly NounSource[]): string {
  return agreed(locations.map((l) => l.resourceNounPlural))?.toLowerCase() ?? 'bays';
}

/** First letter upper-cased ("bays" → "Bays"). */
export function capitalize(word: string): string {
  return word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1);
}
